import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import * as SubframeCore from "@subframe/core";
import { DefaultPageLayout } from "../ui/layouts/DefaultPageLayout";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { Badge } from "../ui/components/Badge";
import { Table } from "../ui/components/Table";
import { IconWithBackground } from "../ui/components/IconWithBackground";
import { Loader } from "../ui/components/Loader";
import { Tooltip } from "../ui/components/Tooltip";
import { Dialog } from "../ui/components/Dialog";
import { FeatherArrowLeft, FeatherX } from "@subframe/core";
import { FeatherDownload } from "@subframe/core";
import { FeatherEdit2 } from "@subframe/core";
import { FeatherRepeat } from "@subframe/core";
import { FeatherTrash2 } from "@subframe/core";
import { FeatherFileText } from "@subframe/core";
import { FeatherAlertTriangle } from "@subframe/core";
import { FeatherPause } from "@subframe/core";
import { journalsAPI, Journal, MakeRecurringRequest } from "../services/api";
import { useActiveLocations } from "../shared/hooks/useActiveLocations";
import MakeRecurringModal from "../components/MakeRecurringModal";
import ActivityLogComponent from "../components/ActivityLogComponent";
import { StatusBadgeForJournals } from "../ui/components/StatusBadgeForJournals";
import {
  useToast,
  ReviewStatusBanner,
  ReviewRequestModal,
  ApArWorkbenchUpdateConfirmModal,
} from "../shared/presentation/components";
import {
  useEntityReviewStatus,
  useJournalReviewData,
} from "../shared/presentation/hooks";
import { useAuth } from "../features/auth/presentation/hooks/useAuth";
import { MODULE_CODES, ACTION_CODES } from "../shared/constants/permissions";
import { ChangeDetails } from "../shared/utils/reviewWorkflow";

const JournalDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { journalId } = useParams<{ journalId: string }>();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const journalType = (location.state as { journalType?: string } | null)?.journalType;
  const isApArJournal = journalType === "ap_ar";
  const reviewStatusEntityType = isApArJournal ? "ap_ar_journal" : "journal";

  // Review status
  const { review, hasPendingReview } = useEntityReviewStatus(
    journalId ? reviewStatusEntityType : null,
    journalId ? parseInt(journalId) : null
  );

  // Journal review data hook for permissions
  const {
    canCreateRecurring,
    needsReviewForCreateRecurring,
    needsReviewForApArDelete,
    hasApprovedReview,
  } = useJournalReviewData({
    moduleCode: MODULE_CODES.JOURNAL,
    entityType: reviewStatusEntityType,
    documentId: journalId || null,
    fetchDocument: async (id: string) => {
      const type = (location.state as { journalType?: string } | null)?.journalType;
      if (type === "ap_ar") {
        const response = await journalsAPI.getApArJournalById(parseInt(id, 10));
        return response.data;
      }
      const response = await journalsAPI.getJournalById(id);
      return response.data;
    },
    isPostedToZoho: (doc: any) =>
      !!doc?.zoho_journalid ||
      !!doc?.tally_remote_id ||
      !!doc?.tally_master_id ||
      !!doc?.posted_to_tally_date,
    initialDocument: null,
  });

  // State management
  const [journal, setJournal] = useState<Journal | null>(null);

  // ZOUR-65 F4: which of our GST registrations this journal is filed under. A
  // NULL `location_id` is the primary, not a blank — that is what an unlocated
  // document means to Zoho, so the primary is what gets shown for it.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
    primary: primaryLocation,
  } = useActiveLocations();
  const journalLocation =
    activeLocations.find(
      (candidate) => candidate.zoho_location_id === (journal?.location_id ?? null),
    ) ?? (journal?.location_id ? null : primaryLocation);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [makeRecurringModalOpen, setMakeRecurringModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [showApArWorkbenchModal, setShowApArWorkbenchModal] = useState(false);

  // Review modal state for create_recurring and ap_ar_delete
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewChangeDetails, setReviewChangeDetails] =
    useState<ChangeDetails | null>(null);
  const [reviewActionCode, setReviewActionCode] = useState<string | null>(null);
  const reviewRequestIsApArDelete =
    reviewActionCode === ACTION_CODES.AP_AR_DELETE;

  // State for showing approved banner temporarily
  const [showApprovedBanner, setShowApprovedBanner] = useState(false);
  const approvedBannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Format account name for display (snake_case -> Title Case, e.g. accounts_payable -> Accounts Payable)
  const formatAccountNameForDisplay = (name: string | null | undefined): string => {
    if (!name) return "";
    if (name.includes("_")) {
      return name
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    }
    return name;
  };

  // Show approved banner when review is approved (on mount or when review changes to approved)
  useEffect(() => {
    if (review?.status === "approved") {
      setShowApprovedBanner(true);

      // Clear any existing timeout
      if (approvedBannerTimeoutRef.current) {
        clearTimeout(approvedBannerTimeoutRef.current);
      }

      // Auto-dismiss after 45 seconds
      approvedBannerTimeoutRef.current = setTimeout(() => {
        setShowApprovedBanner(false);
      }, 45000);
    } else {
      setShowApprovedBanner(false);
    }

    // Cleanup timeout on unmount
    return () => {
      if (approvedBannerTimeoutRef.current) {
        clearTimeout(approvedBannerTimeoutRef.current);
      }
    };
  }, [review?.status]);

  // Fetch journal details on mount (manual or AP/AR based on location.state)
  useEffect(() => {
    const fetchJournalDetails = async () => {
      if (!journalId) {
        setError("No journal ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const type = (location.state as { journalType?: string } | null)?.journalType;
        if (type === "ap_ar") {
          const id = parseInt(journalId, 10);
          if (isNaN(id)) {
            setError("Invalid journal ID");
            return;
          }
          const response = await journalsAPI.getApArJournalById(id);
          setJournal(response.data);
        } else {
          const response = await journalsAPI.getJournalById(journalId);
          setJournal(response.data);
        }
      } catch (err) {
        console.error("Error fetching journal details:", err);
        setError("Failed to fetch journal details");
      } finally {
        setLoading(false);
      }
    };

    fetchJournalDetails();
  }, [journalId, location.state]);

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to format date with time
  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };

  // Calculate totals
  const getTotalDebits = (): number => {
    if (!journal) return 0;
    if (!journal.line_items) return 0;
    return journal.line_items
      .filter((item) => item.is_debit_or_credit === "debit")
      .reduce((sum, item) => sum + Number(item.amount), 0);
  };

  const getTotalCredits = (): number => {
    if (!journal) return 0;
    if (!journal.line_items) return 0;
    return journal.line_items
      .filter((item) => item.is_debit_or_credit === "credit")
      .reduce((sum, item) => sum + Number(item.amount), 0);
  };

  const getDifference = (): number => {
    return Math.abs(getTotalDebits() - getTotalCredits());
  };

  // Handle back navigation
  const handleBack = () => {
    navigate("/dashboard/journal");
  };

  // Handle edit navigation (pass journalType so Edit page can load manual vs AP/AR correctly)
  const handleEdit = () => {
    navigate(`/journal/edit/${journalId}`, {
      state: { journalType: isApArJournal ? "ap_ar" : "manual" },
    });
  };

  // Proceed with delete flow after AP/AR workbench confirm (opens review modal or delete confirm modal)
  const proceedWithDeleteApArFlow = () => {
    if (!journalId || !journal) return;
    if (needsReviewForApArDelete) {
      const rawJson = {
        journal: {
          date: journal.date,
          reference_number: journal.reference_number,
          notes: journal.notes,
          currency_id: journal.currency_id,
          currency_symbol: journal.currency_symbol,
          type: journal.type,
        },
        line_items: journal.line_items || [],
      };
      setReviewChangeDetails({
        action_types: ["ap_ar_delete"],
        raw_json: rawJson,
      });
      setReviewActionCode(ACTION_CODES.AP_AR_DELETE);
      setIsReviewModalOpen(true);
    } else {
      setDeleteConfirmModalOpen(true);
    }
  };

  // Open AP/AR workbench confirm first, then delete confirmation or ReviewRequestModal when review required
  const handleDeleteApArClick = () => {
    if (!journalId || !isApArJournal || !journal) return;
    setShowApArWorkbenchModal(true);
  };

  // Handle actual delete submission from confirmation modal (direct delete only; review path uses ReviewRequestModal)
  const handleDeleteApArConfirm = async () => {
    if (!journalId || !isApArJournal) return;
    const id = parseInt(journalId, 10);
    if (isNaN(id)) return;
    try {
      setLoading(true);
      await journalsAPI.deleteApArJournal(id);
      setDeleteConfirmModalOpen(false);
      showSuccess("AP/AR journal deleted");
      navigate("/dashboard/journal");
    } catch (err: unknown) {
      const msg = err && typeof err === "object" && "message" in err ? String((err as { message: string }).message) : "Failed to delete journal";
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Handle reject button click - opens modal
  const handleRejectClick = () => {
    setRejectModalOpen(true);
  };

  // Handle actual rejection submission from modal
  const handleRejectSubmit = async () => {
    if (!journal || !journalId) {
      showError("No journal to reject");
      return;
    }

    try {
      setLoading(true);
      await journalsAPI.rejectJournal(journalId);
      setRejectModalOpen(false);
      showSuccess("Journal rejected successfully");
      navigate("/dashboard/journal");
    } catch (error: any) {
      console.error("Error rejecting journal:", error);
      const errorMessage =
        error.message || error.data?.message || "Failed to reject journal";
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle make recurring - just open the modal
  const handleMakeRecurring = () => {
    setMakeRecurringModalOpen(true);
  };

  // Handle review modal success for create_recurring or ap_ar_delete
  const handleReviewModalSuccess = () => {
    const wasApArDelete = reviewActionCode === ACTION_CODES.AP_AR_DELETE;
    setIsReviewModalOpen(false);
    setReviewChangeDetails(null);
    setReviewActionCode(null);
    if (wasApArDelete) {
      navigate("/dashboard/journal");
      return;
    }
    // create_recurring: refresh journal data to get updated template information
    if (journalId) {
      journalsAPI
        .getJournalById(journalId)
        .then((response) => {
          setJournal(response.data);
        })
        .catch((err) => {
          console.error("Error fetching updated journal:", err);
        });
    }
  };

  // Handle recurring journal submission
  const handleRecurringSubmit = async (success: boolean, message?: string) => {
    if (success) {
      // Refresh journal data to get updated template information
      if (journalId) {
        try {
          const updatedJournal = await journalsAPI.getJournalById(journalId);
          setJournal(updatedJournal.data);
        } catch (err) {
          console.error("Error fetching updated journal:", err);
        }
      }
      // Show success message
      showSuccess(message || "Journal converted to recurring successfully");
    } else {
      // Show error message
      showError(message || "Failed to make journal recurring");
    }
    setMakeRecurringModalOpen(false);
  };

  // Handle attachment download
  const handleAttachmentDownload = (url: string) => {
    window.open(url, "_blank");
  };

  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // Loading state
  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <Loader />
        </div>
      </DefaultPageLayout>
    );
  }

  // Error state
  if (error || !journal) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <IconWithBackground
              variant="error"
              icon={<FeatherAlertTriangle />}
            />
            <span className="text-heading-2 font-heading-2 text-default-font">
              Failed to Load Journal
            </span>
            <span className="text-body font-body text-subtext-color text-center max-w-md">
              {error || "Journal not found"}
            </span>
            <Button onClick={handleBack}>Back to Journals</Button>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        <div className="flex w-full flex-wrap items-start justify-between border-b border-solid border-neutral-border px-8 py-6">
          <div className="hidden items-center gap-2">
            {/* Breadcrumbs - hidden for now */}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <IconButton icon={<FeatherArrowLeft />} onClick={handleBack} />
            <span className="text-body font-body text-default-font">
              Back to Journals
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* AP/AR: Delete only. Manual: Edit / Reject */}
            {isApArJournal ? (
              <Button
                variant="destructive-secondary"
                icon={<FeatherTrash2 />}
                onClick={handleDeleteApArClick}
                disabled={loading}
              >
                Delete
              </Button>
            ) : (
              <>
                {journal.status !== "rejected" && !hasPendingReview && (
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherEdit2 />}
                    onClick={handleEdit}
                  >
                    Edit
                  </Button>
                )}
                {journal.status === "under_review" && !hasPendingReview && (
                  <Button
                    variant="destructive-secondary"
                    icon={<FeatherX />}
                    onClick={handleRejectClick}
                  >
                    Reject
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 px-8 py-8 overflow-auto">
          {/* Review Status Banner */}
          {review && (
            <div className="w-full -mt-4">
              {(review.status === "pending" ||
                review.status === "rejected" ||
                (review.status === "approved" && showApprovedBanner)) && (
                <ReviewStatusBanner
                  review={review}
                  moduleCode={
                    isApArJournal ? MODULE_CODES.AP_AR_JOURNAL : MODULE_CODES.JOURNAL
                  }
                  currentUserId={user?.id ? parseInt(user.id, 10) : null}
                  onResubmit={() => {
                    if (review.status === "rejected") {
                      setIsReviewModalOpen(true);
                    }
                  }}
                />
              )}
            </div>
          )}

          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-3">
                <span className="text-heading-1 font-heading-1 text-default-font">
                  Journal Entry Details
                </span>
                <StatusBadgeForJournals status={journal.status} />
              </div>
              <span className="text-body font-body text-subtext-color">
                Created on{" "}
                {journal.created_at
                  ? formatDateTime(journal.created_at)
                  : "N/A"}
              </span>
            </div>
            {!isApArJournal &&
              !journal.template_id &&
              journal.status !== "rejected" &&
              !hasPendingReview && (
                <Button
                  className="bg-warning-50 text-warning-700 border-warning-200"
                  variant="neutral-secondary"
                  icon={<FeatherRepeat />}
                  onClick={handleMakeRecurring}
                  disabled={!canCreateRecurring}
                >
                  Make Recurring
                </Button>
              )}
            {journal.template && (
              <div className="flex flex-col items-end gap-2">
                <Badge
                  variant={
                    journal.template?.is_cancelled === "1"
                      ? "error"
                      : journal.template?.is_paused === "1"
                      ? "warning"
                      : "success"
                  }
                  icon={
                    journal.template?.is_cancelled === "1" ? (
                      <FeatherX />
                    ) : journal.template?.is_paused === "1" ? (
                      <FeatherPause />
                    ) : (
                      <FeatherRepeat />
                    )
                  }
                >
                  {journal.template?.is_cancelled === "1"
                    ? "Ended"
                    : journal.template?.is_paused === "1"
                    ? "Paused"
                    : "Recurring Journal"}
                </Badge>
                <span className="text-caption font-caption text-subtext-color">
                  {journal.template.template_name}
                  {" • "}
                  {/* {journal.template.recurrence_frequency.toUpperCase()}
                  {" • "} */}
                  {`From ${formatDateForDisplay(journal.template.starts_on)}`}
                  {journal.template.ends_on &&
                    ` Till ${formatDateForDisplay(journal.template.ends_on)}`}
                  {journal.template?.is_cancelled === "1" &&
                    " • Recurring journal has ended"}
                </span>
              </div>
            )}
          </div>
          <div className="flex w-full items-start gap-6">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Basic Information
                </span>
                <div className="w-full items-start gap-6 grid grid-cols-2">
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Date
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {formatDate(journal.date)}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Journal Number
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {isApArJournal
                        ? journal.journal_number
                        : journal.status === "under_review"
                        ? "Will be Auto-Generated after publishing"
                        : journal.journal_number}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Reference Number
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {journal.reference_number || "—"}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Currency
                    </span>
                    <Badge variant="neutral">{journal.currency_symbol}</Badge>
                  </div>
                  {/* ZOUR-65 F4: the GST registration this journal is filed
                      under. Shown only on a company that has more than one, and
                      never for an AP/AR journal, which takes its location from
                      the document it was raised for. */}
                  {hasMultipleLocations && !isApArJournal && (
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-body font-body text-subtext-color">
                        Location
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {journalLocation
                          ? `${journalLocation.name}${
                              journalLocation.gstin
                                ? ` \u00b7 ${journalLocation.gstin}`
                                : ""
                            }${
                              journalLocation.state_code
                                ? ` (${journalLocation.state_code})`
                                : ""
                            }`
                          : "\u2014"}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col items-start gap-2 col-span-2">
                    <span className="text-body font-body text-subtext-color">
                      Notes
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {journal.notes}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2 col-span-2">
                    <span className="text-body font-body text-subtext-color">
                      Reporting Method
                    </span>
                    <Badge>
                      {journal.type === "both"
                        ? "Accrual and Cash"
                        : journal.type === "cash"
                        ? "Cash Only"
                        : "Bank Only"}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/*  Regular Journal Summary */}
            <div className="flex w-96 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Summary
              </span>
              <div className="flex w-full flex-col items-start gap-3">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Sub Total - Debits
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {journal.currency_symbol}{" "}
                    {getTotalDebits().toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Sub Total - Credits
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {journal.currency_symbol}{" "}
                    {getTotalCredits().toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between border-t border-solid border-neutral-border pt-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Total ({journal.currency_symbol})
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {getTotalDebits().toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Difference
                  </span>
                  <span
                    className={`text-body-bold font-body-bold ${
                      getDifference() < 0.01
                        ? "text-success-600"
                        : "text-error-600"
                    }`}
                  >
                    {journal.currency_symbol}{" "}
                    {getDifference().toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex items-center gap-4">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Line Items
              </span>
              <Badge variant="neutral">
                {journal.line_items?.length || 0} Items
              </Badge>
            </div>
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Account</Table.HeaderCell>
                  <Table.HeaderCell>Description</Table.HeaderCell>
                  <Table.HeaderCell>Contact</Table.HeaderCell>
                  <Table.HeaderCell>Debits</Table.HeaderCell>
                  <Table.HeaderCell>Credits</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {journal.line_items &&
                journal.line_items.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <SubframeCore.Tooltip.Provider>
                        <SubframeCore.Tooltip.Root>
                          <SubframeCore.Tooltip.Trigger asChild={true}>
                            <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                              {formatAccountNameForDisplay(item.account_name) || item.account_name}
                            </span>
                          </SubframeCore.Tooltip.Trigger>
                          <SubframeCore.Tooltip.Portal>
                            <SubframeCore.Tooltip.Content
                              side="top"
                              align="start"
                              sideOffset={4}
                              asChild={true}
                            >
                              <Tooltip>
                                {formatAccountNameForDisplay(item.account_name) || item.account_name}
                              </Tooltip>
                            </SubframeCore.Tooltip.Content>
                          </SubframeCore.Tooltip.Portal>
                        </SubframeCore.Tooltip.Root>
                      </SubframeCore.Tooltip.Provider>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {item.description || "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {item.contact_name?.trim() || "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {item.is_debit_or_credit === "debit"
                          ? `${journal.currency_symbol}${Number(
                              item.amount
                            ).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {item.is_debit_or_credit === "credit"
                          ? `${journal.currency_symbol}${Number(
                              item.amount
                            ).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : "—"}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table>
          </div>

          {journal.attachment_urls && journal.attachment_urls.length > 0 && (
            <div className="flex w-full flex-col items-start gap-6">
              <div className="flex items-center gap-4">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Attachments
                </span>
                <Badge variant="neutral">
                  {journal.attachment_urls.length} Files
                </Badge>
              </div>
              <div className="flex w-full flex-wrap items-start gap-4">
                {journal.attachment_urls.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex w-64 flex-none flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4"
                  >
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <IconWithBackground
                          variant="neutral"
                          size="small"
                          icon={<FeatherFileText />}
                        />
                        <span className="text-body-bold font-body-bold text-default-font truncate">
                          {attachment.filename}
                        </span>
                      </div>
                      <IconButton
                        size="small"
                        icon={<FeatherDownload />}
                        onClick={() =>
                          handleAttachmentDownload(attachment.signed_url)
                        }
                      />
                    </div>
                    <span className="text-caption font-caption text-subtext-color">
                      {(attachment.size / (1024 * 1024)).toFixed(2)} MB •{" "}
                      {attachment.mime_type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Activity Log */}
          {journalId && (
            <ActivityLogComponent
              feature={isApArJournal ? "ap_ar_journal" : "journal"}
              featureId={journalId}
            />
          )}
        </div>
      </div>

      {/* Review Request Modal for create_recurring */}
      <ReviewRequestModal
        open={isReviewModalOpen}
        onOpenChange={setIsReviewModalOpen}
        moduleCode={
          reviewRequestIsApArDelete
            ? MODULE_CODES.AP_AR_JOURNAL
            : MODULE_CODES.JOURNAL
        }
        actionCode={reviewActionCode || ACTION_CODES.CREATE_RECURRING}
        entityType={reviewRequestIsApArDelete ? "ap_ar_journal" : "journal"}
        entityId={journalId ? parseInt(journalId) : 0}
        changeDetails={reviewChangeDetails || undefined}
        onSuccess={handleReviewModalSuccess}
        skipNavigation={true}
      />

      {/* Make Recurring Modal */}
      <MakeRecurringModal
        open={makeRecurringModalOpen}
        onOpenChange={setMakeRecurringModalOpen}
        onSubmit={handleRecurringSubmit}
        journalId={journalId || ""}
        needsReviewForCreateRecurring={needsReviewForCreateRecurring}
        hasApprovedReview={hasApprovedReview}
        onReviewNeeded={(requestData: MakeRecurringRequest) => {
          // Close make recurring modal
          setMakeRecurringModalOpen(false);

          // Prepare field_changes with all recurring fields
          const fieldChanges: Array<{
            field: string;
            old: string;
            new: string;
          }> = [
            {
              field: "is_recurring",
              old: "no",
              new: "yes",
            },
            {
              field: "template_name",
              old: "(empty)",
              new: requestData.template_name || "",
            },
            {
              field: "recurrence_frequency",
              old: "(empty)",
              new: requestData.recurrence_frequency || "",
            },
            {
              field: "recurrence_interval",
              old: "(empty)",
              new: requestData.recurrence_interval?.toString() || "(empty)",
            },
            {
              field: "starts_on",
              old: "(empty)",
              new: requestData.starts_on || "",
            },
            {
              field: "ends_on",
              old: "(empty)",
              new: requestData.ends_on || "(empty)",
            },
            {
              field: "never_expires",
              old: "false",
              new: requestData.never_expires ? "true" : "false",
            },
          ];

          // Add reference_number_prefix if provided
          if (requestData.reference_number_prefix) {
            fieldChanges.push({
              field: "reference_number_prefix",
              old: "(empty)",
              new: requestData.reference_number_prefix,
            });
          }

          // Set review change details with raw_json as the request data
          setReviewChangeDetails({
            raw_json: requestData, // Use the request data as raw_json
            field_changes: fieldChanges,
            action_types: ["create_recurring"],
          });
          setReviewActionCode(ACTION_CODES.CREATE_RECURRING);

          // Show review modal
          setIsReviewModalOpen(true);
        }}
      />

      {/* Reject Journal Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <Dialog.Content className="w-[500px]">
          <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
            <div className="flex w-full items-start justify-between">
              <div className="flex flex-col items-start gap-2">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Reject Journal
                </span>
                <span className="text-body font-body text-subtext-color">
                  Are you sure you want to reject this journal entry?
                </span>
              </div>
              <IconButton
                icon={<FeatherX />}
                onClick={() => setRejectModalOpen(false)}
              />
            </div>

            <div className="flex w-full items-center justify-end gap-2">
              <Button
                variant="neutral-secondary"
                onClick={() => {
                  setRejectModalOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive-primary"
                onClick={handleRejectSubmit}
                disabled={loading}
              >
                {loading ? "Rejecting..." : "Reject Journal"}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>

      {/* AP/AR workbench update confirm - before delete for AP/AR journal */}
      <ApArWorkbenchUpdateConfirmModal
        open={showApArWorkbenchModal}
        onOpenChange={setShowApArWorkbenchModal}
        onConfirm={() => {
          setShowApArWorkbenchModal(false);
          proceedWithDeleteApArFlow();
        }}
        loading={loading}
      />

      {/* Delete AP/AR Journal Confirmation Modal */}
      <Dialog open={deleteConfirmModalOpen} onOpenChange={setDeleteConfirmModalOpen}>
        <Dialog.Content className="w-[500px]">
          <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
            <div className="flex w-full items-start justify-between">
              <div className="flex flex-col items-start gap-2">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Delete AP/AR Journal
                </span>
                <span className="text-body font-body text-subtext-color">
                  Delete this AP/AR journal? This will uncategorize linked Zoho bank transactions.
                </span>
              </div>
              <IconButton
                icon={<FeatherX />}
                onClick={() => setDeleteConfirmModalOpen(false)}
              />
            </div>

            <div className="flex w-full items-center justify-end gap-2">
              <Button
                variant="neutral-secondary"
                onClick={() => setDeleteConfirmModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive-primary"
                onClick={handleDeleteApArConfirm}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </DefaultPageLayout>
  );
};

export default JournalDetailPage;
